/**
 * Zero-dependency toast API usable from Astro <script>s and React islands alike.
 *   import { toast } from '@ui/toast';
 *   toast({ title: 'Payment received', body: '$412.60 from Dave M.', tone: 'done' });
 * Announced via an aria-live region; auto-dismisses (default 5s), pauses on hover, closable.
 */
export type ToastTone = 'neutral' | 'signal' | 'danger' | 'ai' | 'transit' | 'done';
export interface ToastOptions { title: string; body?: string; tone?: ToastTone; duration?: number }

const ICON: Record<ToastTone, string> = {
  neutral: '<circle cx="12" cy="12" r="9.25"/><path d="M12 11v5.5"/><circle cx="12" cy="7.75" r=".9" fill="currentColor" stroke="none"/>',
  signal: '<path d="M5 4h3.2l1.6 4-2 1.3a11 11 0 0 0 6.9 6.9l1.3-2 4 1.6V19a1.5 1.5 0 0 1-1.6 1.5A16.5 16.5 0 0 1 3.5 5.6 1.5 1.5 0 0 1 5 4Z"/>',
  danger: '<path d="M12 3.8 21.2 19.5H2.8Z"/><path d="M12 10v4.2"/><circle cx="12" cy="16.9" r=".9" fill="currentColor" stroke="none"/>',
  ai: '<path d="M12 3.5c.6 4.3 2.2 5.9 6.5 6.5-4.3.6-5.9 2.2-6.5 6.5-.6-4.3-2.2-5.9-6.5-6.5 4.3-.6 5.9-2.2 6.5-6.5Z"/>',
  transit: '<path d="M12 21s-6.75-6.2-6.75-11.25a6.75 6.75 0 0 1 13.5 0C18.75 14.8 12 21 12 21Z"/><circle cx="12" cy="9.75" r="2.4"/>',
  done: '<circle cx="12" cy="12" r="9.25"/><path d="m8 12.3 2.8 2.7L16.2 9.4"/>',
};

function region(): HTMLElement {
  let el = document.querySelector<HTMLElement>('.toast-region');
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast-region';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el);
  }
  return el;
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export function toast({ title, body, tone = 'neutral', duration = 5000 }: ToastOptions): () => void {
  if (typeof document === 'undefined') return () => {};
  const el = document.createElement('div');
  el.className = `toast toast--${tone}`;
  el.innerHTML = `<svg class="toast__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICON[tone]}</svg><div><div class="toast__title">${esc(title)}</div>${body ? `<div class="toast__body">${esc(body)}</div>` : ''}</div><button class="toast__close" type="button" aria-label="Dismiss"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg></button>`;
  const r = region();
  r.appendChild(el);
  while (r.children.length > 4) r.firstElementChild?.remove();
  let timer: number | undefined;
  const close = () => { window.clearTimeout(timer); el.remove(); };
  const arm = () => { if (duration > 0) timer = window.setTimeout(close, duration); };
  el.querySelector('.toast__close')!.addEventListener('click', close);
  el.addEventListener('mouseenter', () => window.clearTimeout(timer));
  el.addEventListener('mouseleave', arm);
  arm();
  return close;
}
